import {
    CreateExtArgs,
    CreateExtensionArgument,
    DalValue,
    DmApis,
    DmStore,
    DocumentDataTypes,
    Extension,
    ExtensionAPI,
    type Store,
    HistoryItem,
    InitializeExtArgs,
    ModelsInitializationType,
    PointerMethods,
    pointerUtils,
    Transaction,
    type ExtensionPublicAPI
} from '@wix/document-manager-core'
import {ReportableError, searchRequestId} from '@wix/document-manager-utils'
import type {
    AppDefinitionId,
    ClientSpecMap,
    ClientSpecMapEntry,
    MetaSiteClientSpecEntry,
    PageList,
    WixCodeModel
} from '@wix/document-services-types'
import {guidUtils} from '@wix/santa-core-utils'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import type {CSaveApi} from './csave/continuousSave'
import type {ModifySiteAppsResponse} from '@wix/ambassador-wix-html-editor-webapp/types'
import type {DocumentServicesModelExtApi} from './documentServicesModel'
import {RESOURCE_TYPES} from '../constants/constants'
import {hasBuilderComponents} from '../utils/appUtils'

const {getGUID} = guidUtils

// should come from platformConstants.APPS.META_SITE.applicationId
const META_SITE_APP_ID = '-666'
const META_SITE_CLIENT_SPEC_MAP_URL = '/_api/msm/v1/meta-site/editor-client-spec-map/'

const {getPointer} = pointerUtils
const pointerType = 'rendererModel'
const clientSpecMapCacheKiller = 'clientSpecMapCacheKiller'
const NO_MATCH: string[] = []
const INDEX_MATCH = [pointerType]

const FIVE_MINUTES = 1000 * 60 * 5

const EVENTS = {
    CLIENT_SPEC_MAP: {
        MIGRATE_APPID_TO_APPDEF: 'MIGRATE_APPID_TO_APPDEF'
    }
}

interface RMApi extends ExtensionAPI {
    siteAPI: {
        getDocumentType(): string
        isTemplate(): boolean
        getInstance(): string
        getRendererModel(): any
        getSiteId(): string
    }
    rendererModel: {
        getInitialPageList(): PageList
        registerToClientSpecMapUpdate(cb: ClientUpdateListener): void
        registerToWixCodeModelUpdate(cb: WixCodeModelUpdateListener): void
        refreshClientSpecMap(): Promise<void>
        updateClientSpecMap(txStore: Store): Promise<any>
        getClientSpecMap(): ClientSpecMap
        getAppComponentDataByWidgetId(appDefinitionId: string, widgetId: string | number): Record<string, any>
        getSiteLanguages(): string[]
        askRemoteEditorsToRefreshClientSpecMap(): void
        onAppsInstalledRemotely(cb: (appData: any) => void): void
        refreshWixInstance(): Promise<string>
        refreshAppInstance(appId: string): Promise<string>
        isWixInstanceExpired(): boolean
        isAppInstanceExpired(appId: string): boolean
        getTimeUntilWixInstanceExpiration(now: number): number
        getSiteId(): string
        getMetaSiteId(): string
        getUserId(): string
        modifySiteApps(apps: {appsToRevoke: AppDefinitionId[]}, branchId: string): Promise<ModifySiteAppsResponse>
        isAppRevoked(appData: any): boolean
        isBuilderComponentModel(): boolean
    }
    metaSiteActions: {
        getRevokeActions(appDefIds: AppDefinitionId[]): Record<string, any>
    }
}

type ClientUpdateListener = (clientSpecMap: ClientSpecMap) => void
type WixCodeModelUpdateListener = (wixCodeModel: WixCodeModel) => void

const createMetaSiteClientSpecMapUrl = (msid: string): string => `${META_SITE_CLIENT_SPEC_MAP_URL}${msid}?https=true`

/**
 * @returns {Extension}
 */
const createExtension = ({environmentContext, logger}: CreateExtensionArgument): Extension => {
    const clientUpdateListeners: ClientUpdateListener[] = []
    const wixCodeModelListeners: WixCodeModelUpdateListener[] = []

    const createPointersMethods = ({dal}: DmApis): PointerMethods => {
        const getUrlFormat = () => getPointer('urlFormatModel', pointerType, {innerPath: ['format']})
        const getForbiddenPageUriSEOs = () =>
            getPointer('urlFormatModel', pointerType, {innerPath: ['forbiddenPageUriSEOs']})
        const getClientSpecMapCacheKiller = () => getPointer(clientSpecMapCacheKiller, pointerType)
        const getClientSpecMap = () => getPointer('clientSpecMap', pointerType)
        const getClientSpecMapEntry = (applicationId: string) =>
            getPointer('clientSpecMap', pointerType, {innerPath: [applicationId]})
        const getClientSpecMapEntryByAppDefId = (appDefinitionId: string) => {
            const csm = dal.get(getPointer('clientSpecMap', pointerType))
            const applicationId = _.get(_.find(csm, {appDefinitionId}), ['applicationId'])
            return getClientSpecMapEntry(applicationId)
        }
        const getClientSpecMapEntriesByPredicate = (predicate: (v: any) => any) => {
            const filteredCSM = predicate(dal.get(getPointer('clientSpecMap', pointerType)))
            return _.map(filteredCSM, appData => getClientSpecMapEntry(appData.applicationId))
        }

        const getSiteMetaData = () => getPointer('siteMetaData', pointerType)
        const isBuilderComponentModel = () =>
            getPointer('siteMetaData', pointerType, {innerPath: ['builderComponentModel']})
        const isResponsive = () => getPointer('siteMetaData', pointerType, {innerPath: ['isResponsive']})
        const isAdaptiveMobileOn = () => getPointer('siteMetaData', pointerType, {innerPath: ['adaptiveMobileOn']})
        const getMetaSiteId = () => getPointer('metaSiteId', pointerType)
        const getSiteId = () => getPointer('siteInfo', pointerType, {innerPath: ['siteId']})
        const getLanguageCode = () => getPointer('languageCode', pointerType)
        const getGeo = () => getPointer('geo', pointerType)
        const getUserId = () => getPointer('userId', pointerType)
        const getSiteTitleSEO = () => getPointer('siteInfo', pointerType, {innerPath: ['siteTitleSEO']})
        const getDocumentType = () => getPointer('siteInfo', pointerType, {innerPath: ['documentType']})
        const getApplicationType = () => getPointer('siteInfo', pointerType, {innerPath: ['applicationType']})
        const getPremiumFeatures = () => getPointer('premiumFeatures', pointerType)
        const getPasswordProtectedPages = () =>
            getPointer('passwordProtectedPages', pointerType, {innerPath: ['pages']})
        const getSitePropertiesInfo = () => getPointer('sitePropertiesInfo', pointerType)
        const getCurrency = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['currency']})
        const getTimeZone = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['timeZone']})
        const getLocale = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['locale']})
        const getRegionalLanguage = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['language']})
        const getMultilingualInfo = () =>
            getPointer('sitePropertiesInfo', pointerType, {innerPath: ['multilingualInfo']})
        const getSiteDisplayName = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['siteDisplayName']})
        const getMediaAuthToken = () => getPointer('mediaAuthToken', pointerType)
        const getWixCodeAppData = () => getPointer('wixCodeModel', pointerType, {innerPath: ['appData']})
        const getRevisionGridAppId = () =>
            getPointer('wixCodeModel', pointerType, {innerPath: ['appData', 'codeAppId']})
        const getWixCodeModel = () => getPointer('wixCodeModel', pointerType)
        const getPageToHashedPassword = () => getPointer('pageToHashedPassword', pointerType, {innerPath: ['pages']})
        const getRoutersPointer = () => getPointer('routers', pointerType)
        const getRoutersConfigMapPointer = () => getPointer('routers', pointerType, {innerPath: ['configMap']})
        const getRouterPointer = (routerId: string) =>
            getPointer('routers', pointerType, {innerPath: ['configMap', routerId]})
        const getPlatformAppsExperiments = () => getPointer('platformAppsExperiments', pointerType)
        const getBlocksExperiments = () => getPointer('blocksExperiments', pointerType)
        const getPageList = () => getPointer('pageList', pointerType)
        return {
            general: {
                getUrlFormat,
                getForbiddenPageUriSEOs,
                getClientSpecMap,
                getSiteMetaData,
                getMetaSiteId,
                isResponsive,
                getPageToHashedPassword,
                getClientSpecMapEntry,
                getClientSpecMapEntryByAppDefId,
                getClientSpecMapEntriesByPredicate,
                getCurrency,
                getRegionalLanguage,
                getTimeZone
            },
            rendererModel: {
                getUrlFormat,
                getForbiddenPageUriSEOs,
                getClientSpecMapCacheKiller,
                getClientSpecMap,
                getClientSpecMapEntry,
                getClientSpecMapEntryByAppDefId,
                getClientSpecMapEntriesByPredicate,
                getSiteMetaData,
                isBuilderComponentModel,
                getPageList,
                getMetaSiteId,
                isResponsive,
                getSiteId,
                getLanguageCode,
                getGeo,
                getUserId,
                getSiteTitleSEO,
                getDocumentType,
                getPremiumFeatures,
                getPasswordProtectedPages,
                getSitePropertiesInfo,
                getCurrency,
                getApplicationType,
                getMultilingualInfo,
                getTimeZone,
                getLocale,
                getRegionalLanguage,
                getMediaAuthToken,
                getPageToHashedPassword,
                getSiteDisplayName,
                getPlatformAppsExperiments,
                getBlocksExperiments,
                isAdaptiveMobileOn
            },
            wixCode: {
                getWixCodeModel,
                getRevisionGridAppId,
                getWixCodeAppData
            },
            routers: {
                getRoutersPointer,
                getRoutersConfigMapPointer,
                getRouterPointer
            }
        }
    }

    const idsWithSignature = new Set(['routers', 'siteMetaData', 'wixCodeModel'])
    const getDocumentDataTypes = (): DocumentDataTypes => ({
        [pointerType]: {
            idsWithSignature
        }
    })

    const onAppsInstalledRemotelyCallbacks: ((appData: object) => void)[] = []
    const {fetchFn, serverFacade} = environmentContext
    let remotelyInstalledAppsToNotifySubscribers: ClientSpecMapEntry[] = []
    const createExtensionAPI = (args: CreateExtArgs): RMApi => {
        const {dal, pointers, coreConfig, extensionAPI, resourceLoader} = args
        const siteExtensionAPI = extensionAPI as DocumentServicesModelExtApi
        let currentClientSpecMapCacheKiller = ''

        const generateClientSpecMapCacheKiller = () => {
            currentClientSpecMapCacheKiller = getGUID()
            return currentClientSpecMapCacheKiller
        }
        const getCurrentClientSpecMapCacheKiller = () => currentClientSpecMapCacheKiller
        const setCurrentClientSpecMapCacheKiller = (newVal: string) => {
            currentClientSpecMapCacheKiller = newVal
        }

        const getAppData = (appId: string) => dal.get(pointers.general.getClientSpecMapEntry(appId)) || {}

        const getMetaSiteId = (): string => dal.get(pointers.general.getMetaSiteId())

        const getAppInstanceExpirationTime = (appId: string, now: number): number => {
            const appData = dal.get(pointers.general.getClientSpecMapEntry(appId)) || {}
            const expirationTimestamp = appData.expirationDate
            if (!expirationTimestamp) {
                return now
            }
            const expirationDate = new Date(expirationTimestamp)
            return expirationDate.getTime() - FIVE_MINUTES
        }

        const getTimeUntilAppInstanceExpiration = (appId: string, now: number) =>
            getAppInstanceExpirationTime(appId, now) - now

        const isAppInstanceExpired = (appId: string) => getTimeUntilAppInstanceExpiration(appId, Date.now()) <= 0

        const createFetchError = (response: Response | null, e: Error): ReportableError =>
            new ReportableError({
                message: 'Cannot fetch client spec map',
                errorType: 'CLIENT_SPEC_MAP_FETCH_FAILURE',
                extras: {
                    status: response?.status,
                    requestId: searchRequestId(response),
                    reason: {name: e.name, message: e.message},
                    isWixInstanceExpired: isAppInstanceExpired(META_SITE_APP_ID),
                    newUrl: true
                }
            })

        const fetchCsmResponse = async (): Promise<Response> =>
            await fetchFn(createMetaSiteClientSpecMapUrl(getMetaSiteId()))

        const fetchClientSpecMap: () => Promise<ClientSpecMap> = async () => {
            let response: Response
            const interaction = 'fetchCsmFromMetaSite'
            try {
                coreConfig.logger.interactionStarted(interaction)
                response = await fetchCsmResponse()
                const result = await response!.json()
                coreConfig.logger.interactionEnded(interaction)
                return result
            } catch (e) {
                const err = createFetchError(response! ?? null, e as Error)
                coreConfig.logger.captureError(err)
                throw err
            }
        }

        const refreshAppInstance = async (appId: string): Promise<string> => {
            const refreshedClientSpecMap = await fetchClientSpecMap()
            const refreshedEntry = refreshedClientSpecMap[appId]
            if (!refreshedEntry) {
                return getAppData(appId)?.instance
            }

            const entry: MetaSiteClientSpecEntry = _.cloneDeep(getAppData(appId))
            _.assign(entry, _.pick(refreshedClientSpecMap[appId], ['instance', 'expirationDate']))
            dal.set(pointers.general.getClientSpecMapEntry(appId), entry)
            return getAppData(appId)?.instance
        }

        const nonRevoked = (appData: ClientSpecMapEntry) => !_.get(appData, ['permissions', 'revoked'], false)
        const revoked = (appData: ClientSpecMapEntry) => _.get(appData, ['permissions', 'revoked'], false)
        const getRevokedClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(revoked)
                .keyBy('applicationId')
                .value()
        const getNewInstalledClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(nonRevoked)
                .filter(cs => _.isNil(existingCSM[_.get(cs, ['applicationId'])]))
                .keyBy('applicationId')
                .value()
        const getReinstalledClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(cs => _.get(existingCSM, [_.get(cs, ['applicationId']), 'permissions', 'revoked'], false))
                .filter(nonRevoked)
                .keyBy('applicationId')
                .value()
        const getUpdatedClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(nonRevoked)
                .filter(cs => {
                    const applicationId = _.get(cs, ['applicationId'])
                    return (
                        !_.isNil(existingCSM[applicationId]) &&
                        !_.isEqual(
                            _.get(existingCSM, [applicationId, 'version']),
                            _.get(refreshedCSM, [applicationId, 'version'])
                        )
                    )
                })
                .keyBy('applicationId')
                .value()

        // @ts-expect-error
        function getInstance(): string {
            const metaSiteData = getAppData(META_SITE_APP_ID)
            if (metaSiteData.instance) {
                return metaSiteData.instance
            }
        }

        const getDocumentTypeVal = () => dal.get(pointers.rendererModel.getDocumentType())

        const getSiteId = () => dal.get(pointers.rendererModel.getSiteId())
        const getUserId = () => dal.get(pointers.rendererModel.getUserId())

        const askRemoteEditorsToRefreshClientSpecMap = () => {
            dal.set(pointers.rendererModel.getClientSpecMapCacheKiller(), {
                cacheKiller: generateClientSpecMapCacheKiller()
            })
        }
        const getClientSpecMap = () => dal.get(pointers.rendererModel.getClientSpecMap())

        const getAppComponentDataByWidgetId = (appDefinitionId: string, widgetId: string | number) => {
            const appData = dal.get(pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId))
            if (!appData) {
                return
            }

            return appData.components?.find((component: Record<string, any>) => widgetId === component.componentId)
                ?.data
        }

        const getSiteLanguages = () => {
            const mlInfo = dal.get(pointers.rendererModel.getMultilingualInfo())
            if (!mlInfo) {
                return []
            }
            const requiredLanguages = mlInfo.translationLanguages.map((lang: any) => lang.languageCode)
            if (mlInfo.originalLanguage.languageCode) {
                return requiredLanguages.concat(mlInfo.originalLanguage.languageCode)
            }
            return requiredLanguages
        }
        const getRevokeActions = (appDefIds: AppDefinitionId[]) => {
            const csm = getClientSpecMap()
            return appDefIds
                .map(appDefinitionId => {
                    const app = _.find(csm, {appDefinitionId})
                    if (!app?.applicationId) {
                        logger.captureError(
                            new ReportableError({
                                errorType: 'appIdMigrationToAppDefIdError',
                                message: 'error on migration process from appId to appDefId',
                                extras: {source: 'metaSiteActions.getRevokeActions', appDefinitionId}
                            })
                        )
                        return null
                    }
                    if (coreConfig.experimentInstance.isOpen('dm_replaceToNewOSinstallAppApi')) {
                        return appDefinitionId
                    }
                    return {
                        type: 'remove',
                        applicationId: app.applicationId
                    }
                })
                .filter(Boolean)
        }

        const modifySiteApps = async (
            {appsToRevoke}: {appsToRevoke: AppDefinitionId[]},
            branchId: string
        ): Promise<ModifySiteAppsResponse> => {
            let metaSiteActions
            if (coreConfig.experimentInstance.isOpen('dm_replaceToNewOSinstallAppApi')) {
                metaSiteActions = {newlyRemovedApps: getRevokeActions(appsToRevoke), newlyAddedApps: []}
            } else {
                metaSiteActions = getRevokeActions(appsToRevoke)
            }
            return serverFacade.modifySiteApps(metaSiteActions, siteExtensionAPI.siteAPI.getSiteRevision(), branchId)
        }

        return {
            siteAPI: {
                getDocumentType: getDocumentTypeVal,
                isTemplate: () => getDocumentTypeVal() === 'Template',
                getInstance,
                getSiteId,
                getRendererModel: () =>
                    _.map(
                        dal.query(pointerType, dal.queryFilterGetters.getRendererModelFilter(pointerType)),
                        (value, id) => ({
                            pointer: getPointer(id, pointerType),
                            value
                        })
                    )
            },
            rendererModel: {
                getInitialPageList: () => dal.get(pointers.rendererModel.getPageList()),
                registerToClientSpecMapUpdate: (cb: ClientUpdateListener) => {
                    if (_.isFunction(cb)) {
                        clientUpdateListeners.push(cb)
                    }
                },
                registerToWixCodeModelUpdate: (cb: WixCodeModelUpdateListener) => {
                    if (_.isFunction(cb)) {
                        wixCodeModelListeners.push(cb)
                    }
                },
                refreshClientSpecMap: async () => {
                    const refreshedCSM = await fetchClientSpecMap()
                    dal.set(pointers.general.getClientSpecMap(), refreshedCSM)
                    askRemoteEditorsToRefreshClientSpecMap()
                },
                getSiteLanguages,
                updateClientSpecMap: async (txStore: DmStore) => {
                    const changes = txStore.getValues()
                    const clientSpecMapCacheKillerItem = _.find(
                        changes,
                        e => e?.key?.type === pointerType && e?.key?.id === clientSpecMapCacheKiller
                    )
                    const received = clientSpecMapCacheKillerItem?.value?.cacheKiller
                    const curr = getCurrentClientSpecMapCacheKiller()

                    if (clientSpecMapCacheKillerItem && received !== curr) {
                        setCurrentClientSpecMapCacheKiller(received)
                        const refreshedCSM = await fetchClientSpecMap()
                        const existingCSM: ClientSpecMap = dal.get(pointers.general.getClientSpecMap())
                        const updatedClientSpecMap = deepClone(existingCSM)

                        const newClientSpecs = getNewInstalledClientSpecs(existingCSM, refreshedCSM)
                        const revokedClientSpecs = getRevokedClientSpecs(existingCSM, refreshedCSM)
                        const reinstalledClientSpecs = getReinstalledClientSpecs(existingCSM, refreshedCSM)
                        const updatedClientSpecs = getUpdatedClientSpecs(existingCSM, refreshedCSM)

                        remotelyInstalledAppsToNotifySubscribers.push(
                            ..._.uniqBy(
                                _.values(newClientSpecs).concat(_.values(reinstalledClientSpecs)),
                                'applicationId'
                            )
                        )
                        _.assign(
                            updatedClientSpecMap,
                            newClientSpecs,
                            revokedClientSpecs,
                            reinstalledClientSpecs,
                            updatedClientSpecs
                        )

                        txStore.set(pointers.general.getClientSpecMap(), updatedClientSpecMap)

                        if (dal.get(pointers.rendererModel.isBuilderComponentModel())) {
                            const newlyInstalledApps = Object.values(newClientSpecs).concat(
                                Object.values(reinstalledClientSpecs),
                                Object.values(updatedClientSpecs)
                            )
                            const appsWithBuilderComponents = newlyInstalledApps.filter(hasBuilderComponents)

                            if (appsWithBuilderComponents.length > 0) {
                                const appDefIds = appsWithBuilderComponents
                                    .map(app => app.appDefinitionId)
                                    .filter(Boolean)

                                if (appDefIds.length > 0) {
                                    try {
                                        await resourceLoader.load(RESOURCE_TYPES.SCHEMA, appDefIds)
                                    } catch (error) {
                                        coreConfig.logger.captureError(
                                            new ReportableError({
                                                errorType: 'SCHEMA_LOADING_FAILURE',
                                                message: 'Failed to load schemas for newly installed remote apps',
                                                extras: {
                                                    appDefIds,
                                                    error: error instanceof Error ? error.message : String(error)
                                                }
                                            })
                                        )
                                    }
                                }
                            }
                        }
                    }
                },
                getClientSpecMap,
                getAppComponentDataByWidgetId,
                refreshWixInstance: async () => refreshAppInstance(META_SITE_APP_ID),
                refreshAppInstance,
                isWixInstanceExpired: () => isAppInstanceExpired(META_SITE_APP_ID),
                isAppInstanceExpired,
                getTimeUntilWixInstanceExpiration: (now: number) =>
                    getTimeUntilAppInstanceExpiration(META_SITE_APP_ID, now),
                askRemoteEditorsToRefreshClientSpecMap,
                onAppsInstalledRemotely: (cb: (appData: any) => void) => {
                    onAppsInstalledRemotelyCallbacks.push(cb)
                },
                getSiteId,
                getMetaSiteId,
                getUserId,
                modifySiteApps,
                isAppRevoked: revoked,
                isBuilderComponentModel: () => dal.get(pointers.rendererModel.isBuilderComponentModel())
            },
            metaSiteActions: {
                getRevokeActions
            }
        }
    }
    const createPublicAPI = ({extensionAPI}: DmApis): ExtensionPublicAPI => {
        const {rendererModel} = extensionAPI as RMApi
        return {
            rendererModel: {
                refreshClientSpecMap: rendererModel.refreshClientSpecMap
            }
        }
    }
    const createFilters = () => ({
        getRendererModelFilter: (namespace: string): string[] => {
            if (namespace === pointerType) {
                return INDEX_MATCH
            }
            return NO_MATCH
        }
    })

    const initialState = {
        [pointerType]: {
            sitePropertiesInfo: {
                multilingualInfo: {
                    originalLanguage: {}
                }
            }
        }
    }

    const createPostTransactionOperations = () => ({
        rendererModel: (documentTransaction: Transaction) => {
            if (!_.isEmpty(remotelyInstalledAppsToNotifySubscribers)) {
                for (const cb of onAppsInstalledRemotelyCallbacks) {
                    cb(remotelyInstalledAppsToNotifySubscribers)
                }

                remotelyInstalledAppsToNotifySubscribers = []
            }
            const rendererModelOperations = documentTransaction.items.filter(e => e?.key?.type === pointerType)
            _.forEach(rendererModelOperations, (item: HistoryItem) => {
                const {
                    value,
                    key: {id: pointerId}
                } = item
                if (pointerId === 'clientSpecMap') {
                    _.forEach(clientUpdateListeners, cb => cb(value))
                }
                if (pointerId === 'wixCodeModel') {
                    _.forEach(wixCodeModelListeners, cb => cb(value))
                }
            })
        }
    })

    const initializeModels = (initialStore: DmStore, initialModels: ModelsInitializationType) => {
        const {
            rendererModel: initialRendererModelFromServer,
            documentServicesModel: {signatures = {}}
        } = initialModels
        for (const idWithSig of idsWithSignature) {
            if (!initialRendererModelFromServer[idWithSig] && signatures[`rendererModel.${idWithSig}`]) {
                initialRendererModelFromServer[idWithSig] = {}
            }
        }
        _.forEach(initialRendererModelFromServer, (value: DalValue, key) => {
            const itemPointer = getPointer(key, 'rendererModel')
            switch (key) {
                case 'urlFormatModel': {
                    let {forbiddenPageUriSEOs} = value
                    if (_.isArray(forbiddenPageUriSEOs)) {
                        forbiddenPageUriSEOs = _(forbiddenPageUriSEOs)
                            .keyBy()
                            .mapValues(() => true)
                            .value()
                    }

                    const urlFormatModelValue = {...value, forbiddenPageUriSEOs}
                    initialStore.set(itemPointer, urlFormatModelValue)
                    return
                }

                case 'passwordProtectedPages': {
                    const wrappedPasswordProtectedPages = _.isArray(value) ? {pages: value} : value
                    initialStore.set(itemPointer, wrappedPasswordProtectedPages)
                    return
                }

                default:
                    const signature = signatures[`rendererModel.${key}`]
                    if (idsWithSignature.has(key) && signature) {
                        const signedValue = {...value, metaData: _.clone(value.metaData)}
                        _.setWith(signedValue, ['metaData', 'sig'], signature, Object)
                        initialStore.set(itemPointer, signedValue)
                    } else {
                        initialStore.set(itemPointer, value)
                    }
            }
        })
    }

    const initialize = async ({extensionAPI, eventEmitter, dal, pointers, coreConfig}: InitializeExtArgs) => {
        const {continuousSave} = extensionAPI as CSaveApi
        const {rendererModel} = extensionAPI as RMApi
        continuousSave?.registerToTransactionApproved(rendererModel.updateClientSpecMap)

        eventEmitter.on(
            EVENTS.CLIENT_SPEC_MAP.MIGRATE_APPID_TO_APPDEF,
            (data: any, reportErrorOnRemigrationSource?: string) => {
                if (data?.appId && !data?.appDefinitionId) {
                    const specMapEntry = dal.get(pointers.general.getClientSpecMapEntry(data?.appId)) || {}
                    if (specMapEntry?.appDefinitionId) {
                        _.set(data, ['appDefinitionId'], specMapEntry.appDefinitionId)
                    }
                    if (reportErrorOnRemigrationSource) {
                        coreConfig.logger.captureError(
                            new ReportableError({
                                errorType: 'menuUpdateAppDefIdMigration',
                                message: `${reportErrorOnRemigrationSource}: data with appId needed appDefinitionId remigration`,
                                extras: {
                                    appId: data.appId,
                                    migratedAppDefinitionId: data.appDefinitionId
                                }
                            })
                        )
                    }
                }
            }
        )
    }

    return {
        name: 'rendererModel',
        dependencies: new Set(['schemaResourceLoader']),
        createPointersMethods,
        getDocumentDataTypes,
        initialState,
        createExtensionAPI,
        createPublicAPI,
        createFilters,
        createPostTransactionOperations,
        initialize,
        initializeModels,
        EVENTS
    }
}

export {META_SITE_APP_ID, createExtension, pointerType, RMApi, createMetaSiteClientSpecMapUrl, EVENTS}
